import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import IconTrash from '@/assets/icons/icon_trash_red_22.svg?react';
import IconEdit from '@/assets/icons/icon_edit_22.svg?react';
import { removeBankDetail } from '@/store/services/paymentServices';
import { getBankDetailsList } from '@/store/actions/paymentActions';
import { RoundLink } from '@/components/UI/RoundLink';
import { ACCOUNT_TYPES, EMPTY_DASH } from '@/helpers/constants';
import { APP_LINKS } from '@/helpers/links';
import { setModals } from '@/store/actions/commonActions';
import { MODAL_TYPES } from '@/components/ModalProvider';
import { Tooltip } from '@/components/Tooltip';
import { useGetMe } from '@/hooks/shared/useGetMe';
import s from './index.module.scss';

const STATUSES_T = t => ({
  pending: t('statuses.pending'),
  approved: t('statuses.approved'),
  rejected: t('statuses.rejected'),
  deleted: t('statuses.deleted'),
});

export const BankDetailsTable = () => {
  const dispatch = useDispatch();
  const { bankDetailsList } = useSelector(state => state.paymentStore);
  const { isMainFounder } = useGetMe();
  const { t } = useTranslation('bank-accounts');
  const isEmpty = !bankDetailsList?.length;
  const STATUSES = STATUSES_T(t);

  useEffect(() => {
    dispatch(getBankDetailsList());
  }, []);

  const onRemove = async ({ id }) => {
    dispatch(
      setModals({
        type: MODAL_TYPES.confirm_modal,
        onDelete: async () => {
          await removeBankDetail(id);
          dispatch(getBankDetailsList());
          dispatch(setModals([]));
        },
        title: t('table.are_you_sure_delete_your_bank_account'),
        desc: t('table.delete_bank_account_confirmation'),
      }),
    );
  };

  return (
    <>
      <div className={s.wrapper}>
        <h1 className="f-26 f-500">{t('table.current_bank_accounts')}</h1>

        {isEmpty ? (
          <div className={classnames(s.empty, 'f-20')}>{t('table.no_bank_accounts')}</div>
        ) : (
          <div className={s.table__wrapper}>
            <div className={s.table__inner}>
              <div className={s.table}>
                <div className={s.table__thead}>
                  <div className={s.table__thead_col}>{t('table.bank_account')}</div>
                  <div className={s.table__thead_col}>{t('table.currency')}</div>
                  <div className={s.table__thead_col}>{t('table.status')}</div>
                  {isMainFounder && <div className={s.table__thead_col}>{t('table.actions')}</div>}
                </div>

                <div className={s.table__tbody}>
                  {bankDetailsList.map((item, index) => (
                    <div className={s.table__tbody_row} key={item.id || index}>
                      <div className={s.table__tbody_col}>
                        <div className={s.row}>
                          <span>{item.account_number}</span>
                          <span className={s.badge}>
                            {item.main_account_type === ACCOUNT_TYPES.business
                              ? t('table.business')
                              : t('table.personal')}
                          </span>
                        </div>
                      </div>
                      <div className={s.table__tbody_col}>
                        {item.account_currency || EMPTY_DASH}
                      </div>
                      <div className={s.table__tbody_col}>
                        <div className={s.status}>
                          <span
                            className={item.review_status}
                            style={{ textTransform: 'capitalize' }}
                          >
                            {STATUSES[item.review_status]}
                          </span>
                          {item.review_status === 'rejected' && (
                            <Tooltip
                              renderContent={() => (
                                <div className={classnames(s.status_tooltip, 'f-16 f-400')}>
                                  {item.rejected_reason}
                                </div>
                              )}
                            />
                          )}
                        </div>
                      </div>
                      {isMainFounder && (
                        <div className={s.table__tbody_col}>
                          <div className={s.controls}>
                            <Link
                              to={APP_LINKS.bankAccountsEdit(item.id)}
                              className={classnames(
                                s.button,
                                s.button_edit,
                                !['rejected', 'pending'].includes(item.review_status) &&
                                  s.button_hidden,
                              )}
                              disabled={!['rejected', 'pending'].includes(item.review_status)}
                            >
                              <IconEdit /> <span>{t('table.edit')}</span>
                            </Link>
                            <button
                              type="button"
                              onClick={() => onRemove(item)}
                              className={classnames(s.button, s.button_remove)}
                            >
                              <IconTrash /> <span>{t('table.delete')}</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {isMainFounder && (
        <RoundLink
          type="button"
          path={APP_LINKS.bankAccountsCreate}
          className={s.button_add}
          label={t('table.add_new_bank_account')}
        />
      )}
    </>
  );
};
